import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, CheckCircle, UserPlus, Star } from "lucide-react";
import axios from "axios";
import { PILLAR_NAME_LIST } from '../data/pillars';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MentorApplicationPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_title: '',
    organization: '',
    years_experience: '',
    expertise_areas: [],
    industry_background: '',
    education_background: '',
    certifications: '',
    mentoring_experience: '',
    availability: '',
    why_mentor: '',
    previous_mentoring: '',
    references: '',
    linkedin_profile: '',
    website: '',
    additional_info: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const expertiseOptions = PILLAR_NAME_LIST;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleExpertiseChange = (expertise) => {
    const updated = formData.expertise_areas.includes(expertise)
      ? formData.expertise_areas.filter(area => area !== expertise)
      : [...formData.expertise_areas, expertise];
    
    setFormData({
      ...formData,
      expertise_areas: updated
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        years_experience: parseInt(formData.years_experience)
      };
      
      const response = await axios.post(`${API}/mentor-application`, submitData);
      setSubmitSuccess(true);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        current_title: '',
        organization: '',
        years_experience: '',
        expertise_areas: [],
        industry_background: '',
        education_background: '',
        certifications: '',
        mentoring_experience: '',
        availability: '',
        why_mentor: '',
        previous_mentoring: '',
        references: '',
        linkedin_profile: '',
        website: '',
        additional_info: ''
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24">
        {/* Hero Banner */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1758522276267-b3472583e954?auto=format&fit=crop&w=1920"
              alt="Wisdom Transfer - Mentorship and Guidance"
              className="w-full h-full object-cover"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/70 via-indigo-900/65 to-blue-900/70"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                Become a{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                  CNESS Mentor
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed">
                Guide organizations on their conscious business journey and help shape the future of sustainable enterprise. Share your expertise and make a meaningful impact.
              </p>
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-8">
                  Mentor Application
                </h2>
                <p className="text-xl text-gray-600">
                  Tell us about your experience and expertise. We'll review your application and get back to you within 48 hours.
                </p>
              </div>
              
              <Card className="shadow-xl">
                <CardContent className="p-12">
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Information */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Star className="h-6 w-6 text-purple-500 mr-3" />
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                          <input 
                            type="text" 
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Your full name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                          <input 
                            type="email" 
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="your.email@company.com"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                          <input 
                            type="tel" 
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Current Title *</label>
                          <input 
                            type="text" 
                            name="current_title"
                            value={formData.current_title}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Chief Executive Officer"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Organization *</label>
                          <input 
                            type="text" 
                            name="organization"
                            value={formData.organization}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Your organization name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
                          <input 
                            type="number" 
                            name="years_experience"
                            value={formData.years_experience}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="10"
                            min="1"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Expertise Areas */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Star className="h-6 w-6 text-purple-500 mr-3" />
                        Areas of Expertise
                      </h3>
                      <p className="text-gray-600 mb-4">Select the pillars where you have the most expertise (select at least one):</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {expertiseOptions.map((expertise) => (
                          <label key={expertise} className="flex items-center p-4 border border-gray-300 rounded-lg hover:border-purple-500 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.expertise_areas.includes(expertise)}
                              onChange={() => handleExpertiseChange(expertise)}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <span className="ml-3 text-gray-700">{expertise}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Professional Background */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Star className="h-6 w-6 text-purple-500 mr-3" />
                        Professional Background
                      </h3>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Industry Background *</label>
                          <input 
                            type="text" 
                            name="industry_background"
                            value={formData.industry_background}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Technology, Healthcare, Finance, etc."
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Education Background</label>
                          <input 
                            type="text" 
                            name="education_background"
                            value={formData.education_background}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="MBA, PhD, Bachelor's degree, etc."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Relevant Certifications</label>
                          <input 
                            type="text" 
                            name="certifications"
                            value={formData.certifications}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="B-Corp, PMP, LEED, etc."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Mentoring Information */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Star className="h-6 w-6 text-purple-500 mr-3" />
                        Mentoring Information
                      </h3>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Mentoring Experience *</label>
                          <textarea 
                            rows="4" 
                            name="mentoring_experience"
                            value={formData.mentoring_experience}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Describe your experience in coaching, mentoring, or guiding organizations..."
                            required
                          ></textarea>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Availability *</label>
                          <select 
                            name="availability"
                            value={formData.availability}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            required
                          >
                            <option value="">Select your availability</option>
                            <option value="5-10 hours per month">5-10 hours per month</option>
                            <option value="10-20 hours per month">10-20 hours per month</option>
                            <option value="20+ hours per month">20+ hours per month</option>
                            <option value="Project-based">Project-based</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Why do you want to become a CNESS mentor? *</label>
                          <textarea 
                            rows="4" 
                            name="why_mentor"
                            value={formData.why_mentor}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Share your motivation for joining the CNESS mentor program..."
                            required
                          ></textarea>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Previous Mentoring Examples</label>
                          <textarea 
                            rows="3" 
                            name="previous_mentoring"
                            value={formData.previous_mentoring}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Examples of organizations or individuals you've mentored..."
                          ></textarea>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                        <Star className="h-6 w-6 text-purple-500 mr-3" />
                        Additional Information
                      </h3>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn Profile</label>
                          <input 
                            type="url" 
                            name="linkedin_profile"
                            value={formData.linkedin_profile}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="https://linkedin.com/in/yourprofile"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                          <input 
                            type="url" 
                            name="website"
                            value={formData.website}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="https://yourwebsite.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">References</label>
                          <textarea 
                            rows="3" 
                            name="references"
                            value={formData.references}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Professional references (name, title, organization, contact info)..."
                          ></textarea>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                          <textarea 
                            rows="4" 
                            name="additional_info"
                            value={formData.additional_info}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Anything else you'd like us to know about your application..."
                          ></textarea>
                        </div>
                      </div>
                    </div>
                    
                    {submitSuccess && (
                      <div className="p-6 bg-green-50 border border-green-200 rounded-lg flex items-center">
                        <CheckCircle className="h-6 w-6 text-green-500 mr-4" />
                        <div>
                          <p className="text-green-700 font-semibold">Application Submitted Successfully!</p>
                          <p className="text-green-600">Thank you for your interest in becoming a CNESS mentor. We'll review your application and get back to you within 48 hours.</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="text-center">
                      <Button 
                        type="submit"
                        disabled={isSubmitting || formData.expertise_areas.length === 0}
                        size="lg" 
                        className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-12"
                      >
                        {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                      <p className="text-sm text-gray-500 mt-4">
                        We'll respond within 48 hours • All information is confidential
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MentorApplicationPage;